import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MailSyncState } from '@worki/shared';
import { scanRemainingSeconds } from '../lib/mailScan.js';
import { cn } from '../lib/cn.js';

/**
 * INV-003 — the mailbox scan, while it is running.
 *
 * A first scan reads four years of mail and opens every message that looks like
 * an invoice, so on a real mailbox it is many minutes of work. The only signal
 * was a 16px refresh icon spinning in the corner of the account row, which is
 * indistinguishable from nothing having happened — the screen looked identical
 * before, during and after.
 *
 * So the account row is replaced, for as long as the scan runs, by this. Three
 * things are on it and each answers a different question: the mark says work is
 * happening, the counters say how much is left, and the clock says the scan has
 * not hung. The clock is the one that cannot be dropped — an indeterminate
 * animation on its own cannot tell a slow scan from a dead one.
 *
 * Every number comes from the server's own job state, so this survives a reload
 * mid-scan: the panel comes back with the scan where it actually is, not
 * restarted from zero.
 */
export function MailScanPanel({
  email,
  state,
  className,
}: {
  email: string;
  state: MailSyncState;
  className?: string;
}) {
  const { t } = useTranslation();
  const seconds = useElapsedSeconds(state.startedAt);

  // Null until the mailbox has been searched — there is no fraction to show
  // yet, and a bar filling against an unknown total is a bar that lies.
  const progress = state.total !== null && state.total > 0 ? state.scanned / state.total : null;

  /*
   * How much longer, from how long it has taken so far.
   *
   * A count of seconds spent answers the wrong question: somebody watching a
   * scan wants to know whether to wait or to come back, and "94 seconds" says
   * nothing about that. The rate this scan has actually run at is the only
   * honest basis for the answer, so the estimate is the remaining messages at
   * the speed of the ones already done, and it is withheld until there is
   * enough of a scan to divide by — before that it would be a number invented
   * from two or three messages and a cold connection.
   */
  const remaining=scanRemainingSeconds(state,seconds);

  return (
    <div role="status" aria-live="polite" className={cn('glass-card rounded-lg p-3 border-primary/30', className)}>
      <div className="flex items-center gap-3">
        <MailScanMark className="h-16 w-20 shrink-0" />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{t(state.phase==='recovering' ? 'invoices.scanRecovering' : state.phase==='refining' ? 'invoices.scanRefining' : state.phase==='queued' || state.queuedWork ? 'invoices.scanQueued' : 'invoices.scanning')}</p>
          <p
            className="text-[11px] text-muted-foreground truncate ltr-nums"
            dir="ltr"
            style={{ textAlign: 'start' }}
          >
            {email}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 ltr-nums">
            {/* Before anything can be opened the mailbox has to be walked, and
                on four years of mail that walk is the first minutes of the scan. It
                used to report itself as "searching…" and nothing else, which is
                the one thing a user cannot tell from a hang. */}
            {state.total === null
              ? state.examined > 0
                ? t('invoices.scanExamined', { examined: state.examined })
                : t('invoices.scanSearching')
              : t('invoices.scanProgress', { scanned: state.scanned, total: state.total })}
            {state.imported > 0 && ` · ${t('invoices.scanFound', { count: state.imported })}`}
          </p>
        </div>

        <span className="text-xs text-muted-foreground shrink-0 ltr-nums tabular-nums self-start text-end">
          {/* What is left, once it can be worked out; what has gone, until
              then. One number, because two clocks side by side is arithmetic
              the reader should not have to do. */}
          {remaining === null ? t('invoices.elapsed', { seconds }) : t('invoices.remaining', { time: durationLabel(remaining, t) })}
        </span>
      </div>

      {/* Determinate only once the total is known; until then the mark above is
          the whole of the progress report, which is the honest amount. */}
      {progress !== null && (
        <div
          className="mt-2.5 h-1 rounded-full bg-muted overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
            style={{ width: `${Math.min(100, Math.round(progress * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Seconds since the scan started, counted from the server's timestamp rather
 * than from when this component mounted — a panel remounted by a reload has to
 * pick the count back up, not start it again.
 */
function useElapsedSeconds(startedAt: string): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  return Math.max(0, Math.round((now - new Date(startedAt).getTime()) / 1_000));
}

/**
 * Seconds, said the way somebody waiting would say them.
 *
 * An estimate carries its own error, so it is rounded to the unit it is honest
 * at: under a minute stays in seconds, and above it goes to whole minutes —
 * "about 4 minutes" is a promise a scan can keep, "3:47" is not.
 */
function durationLabel(seconds: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (seconds < 60) return t('invoices.secondsShort', { count: seconds });
  return t('invoices.minutesShort', { count: Math.max(1, Math.round(seconds / 60)) });
}

/**
 * The mark itself: a page off the stack, under a read head that sweeps it top
 * to bottom while each line lights as the head crosses it.
 *
 * Every colour is a theme variable rather than a literal, because the runtime
 * theme picker (src/lib/theme.ts) repaints `--primary` and the mark has to
 * follow the rest of the screen when it does. The geometry is symmetric about
 * the vertical axis on purpose: SVG does not mirror under `dir="rtl"`, so
 * anything weighted to one side would sit the wrong way round in Hebrew.
 *
 * The mark itself claims nothing — no ticks landing, no rows filling in, no
 * count of anything. It says work is happening and stops there. What was
 * actually found is on the panel around it, where the numbers come from the
 * server; an illustration that mimed finding things would be inventing them for
 * however long it ran before the real ones arrived.
 */
function MailScanMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 96"
      className={className}
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* The head's trailing glow — brightest at the line, gone 22 units up. */}
        <linearGradient id="mailScanBeam" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          <stop offset="70%" stopColor="hsl(var(--primary))" stopOpacity="0.16" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.42" />
        </linearGradient>
        {/* Keeps the beam inside the page instead of over the whole box. */}
        <clipPath id="mailScanSheet">
          <rect x="34" y="8" width="52" height="80" rx="6" />
        </clipPath>
      </defs>

      {/* The messages still queued behind the one being read. */}
      <rect
        x="34"
        y="8"
        width="52"
        height="80"
        rx="6"
        fill="hsl(var(--muted))"
        transform="rotate(-6 60 48)"
        opacity="0.55"
      />
      <rect
        x="34"
        y="8"
        width="52"
        height="80"
        rx="6"
        fill="hsl(var(--muted))"
        transform="rotate(3.5 60 48)"
        opacity="0.8"
      />

      <rect
        x="34"
        y="8"
        width="52"
        height="80"
        rx="6"
        fill="hsl(var(--card))"
        stroke="hsl(var(--border))"
        strokeWidth="1"
      />

      {/* Supplier, four body lines, then the total under a rule — the shape of
          an invoice at a glance, which is all the mark needs to be read as one. */}
      <rect className="mail-scan__line" x="42" y="18" width="22" height="4" rx="1.5" style={{ animationDelay: '0.45s' }} />
      <rect className="mail-scan__line" x="42" y="28" width="36" height="3" rx="1.5" style={{ animationDelay: '0.7s' }} />
      <rect className="mail-scan__line" x="42" y="35" width="28" height="3" rx="1.5" style={{ animationDelay: '0.85s' }} />
      <rect className="mail-scan__line" x="42" y="42" width="36" height="3" rx="1.5" style={{ animationDelay: '1s' }} />
      <rect className="mail-scan__line" x="42" y="49" width="20" height="3" rx="1.5" style={{ animationDelay: '1.15s' }} />
      <line x1="42" y1="59" x2="78" y2="59" stroke="hsl(var(--border))" strokeWidth="1" />
      <rect className="mail-scan__line" x="42" y="65" width="12" height="3" rx="1.5" style={{ animationDelay: '1.5s' }} />
      <rect className="mail-scan__line" x="60" y="64" width="18" height="5" rx="1.5" style={{ animationDelay: '1.5s' }} />

      <g clipPath="url(#mailScanSheet)">
        <g className="mail-scan__beam">
          <rect x="34" y="-22" width="52" height="22" fill="url(#mailScanBeam)" />
          <rect x="34" y="-0.6" width="52" height="1.2" fill="hsl(var(--primary))" opacity="0.85" />
        </g>
      </g>

      {/* Scanner framing. Four corners, so the page reads as something held
          under an instrument rather than a page that happens to be glowing. */}
      <g
        className="mail-scan__frame"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <path d="M30 12v-4h5" />
        <path d="M90 12v-4h-5" />
        <path d="M30 84v4h5" />
        <path d="M90 84v4h-5" />
      </g>
    </svg>
  );
}
